import React, { ChangeEvent, useState } from 'react';
import { 
    Box, 
    Button, 
    Divider, 
    Flex, 
    useRadioGroup, 
    Text, 
    Alert,
    AlertIcon,
    AlertDescription,
    VStack,
    Icon,
    Badge,
    StackDivider,
} from '@chakra-ui/react';
import CustomButton from '../../../components/ui/buttons/Button';
import CustomEditableInput from '../../../components/ui/forms/EditableInput';
import { BsFillShieldLockFill } from 'react-icons/bs';
import { RadioCard } from '../../../components/ui/radio/RadioCard';
import { twoFactorOptions } from '../../../utilities/dummy';

export default function Security() {
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [twoFactorValue, setTwoFactorValue] = useState<string>('')

    const { getRootProps, getRadioProps } = useRadioGroup({
        name: '',
        defaultValue: '',
        onChange: handleTwoFactorOptionChange,
    });    
    const group = getRootProps();

    function handleTwoFactorOptionChange(val: string) {
        setTwoFactorValue(val)
    }

    function handlePasswordInputChange(e: ChangeEvent<HTMLInputElement>) {
        const name = e.target.name;
        const value = e.target.value;

        setPasswords((prevState) => ({
            ...prevState,
            [name]: value
        }))
    }

    return (
        <Box paddingX={'4'} paddingBottom='6'>
            <Box>
                <Flex marginBottom={'2'} paddingBottom='2' paddingTop='2' justifyContent={'space-between'} alignItems='center'>
                    <Text fontSize='lg' color='gray.600' fontWeight={'bold'}>Change password</Text>
                    <Box>
                        <CustomButton 
                            type='button'
                            cta='outline' 
                            variant={'outline'}
                            size='sm'
                            colorScheme='blue'
                            label='Update password'
                            onClick={() => setIs2FAEnabled(!is2FAEnabled)}
                        />
                    </Box>
                </Flex>
                <Divider />
                <Box marginTop={'4'} maxW={'lg'}>
                    <CustomEditableInput 
                        isEditableButtons={false} 
                        formControlProps={{ marginBottom: '6'}}
                        label='Current Password' 
                        value={passwords.currentPassword} 
                        id='currentPassword' 
                        onChange={handlePasswordInputChange}
                        name='currentPassword'
                        placeholder='Enter current password'
                    />
                    <CustomEditableInput 
                        isEditableButtons={false} 
                        formControlProps={{ marginBottom: '6'}}
                        label='New Password' 
                        value={passwords.newPassword} 
                        id='newPassword' 
                        name='newPassword'
                        placeholder='Enter new password'
                        onChange={handlePasswordInputChange}
                    />
                    <CustomEditableInput 
                        isEditableButtons={false} 
                        formControlProps={{ marginBottom: '6'}}
                        label='Confirm New Password' 
                        value={passwords.confirmNewPassword} 
                        id='confirmNewPassword' 
                        name='confirmNewPassword'
                        placeholder='Confirm new password'
                        onChange={handlePasswordInputChange}
                    />
                </Box>
            </Box>

            <Box marginTop='8'>
                <Flex paddingTop='0' justifyContent={'space-between'} alignItems='center'>
                    <Text marginBottom={'2'} marginY={'4'} fontSize='lg' color='gray.600' fontWeight={'bold'}>Two-factor authentication</Text>
                    <Box>
                        <CustomButton 
                            type='button'
                            cta='outline' 
                            variant={'outline'}
                            size='sm'
                            colorScheme='blue'
                            label={is2FAEnabled ? 'Enabled': "Disabled"}
                            onClick={() => setIs2FAEnabled(!is2FAEnabled)}
                        />
                    </Box>
                </Flex>
                <Divider />
                <Box paddingY={'6'}>
                    <Alert status='info' borderRadius={'md'} background='gray.100'>
                        <AlertIcon as={BsFillShieldLockFill} color={'blue.400'} width='16px' height={'16px'} />
                        <AlertDescription>
                            <Box marginLeft={'2'}>
                                <Text fontSize='md' fontWeight={'semibold'}>Secure your account</Text>
                                <Text fontSize={'sm'} color='gray.500'>
                                    Two-factor authentication adds an extra layer of security to your account. When you log in, you'll need to provide a 6-digit code.
                                    <Button 
                                        className='text-underline' 
                                        padding={'0'} 
                                        height='0' 
                                        colorScheme={'blue'} 
                                        variant='ghost'
                                        marginLeft={'1'}
                                        size='sm'
                                    >
                                        Learn more
                                    </Button>
                                </Text>
                            </Box>
                        </AlertDescription>
                    </Alert>
                </Box>
                <Box>
                    <Text marginBottom={'2'} fontWeight={'semibold'}>Two-factor options</Text>
                    <Divider />
                    <VStack 
                        spacing={1} 
                        justifyContent='flex-start' 
                        align='stretch'
                        marginTop={'6'}
                        divider={<StackDivider borderColor='gray.200' />}
                        {...group}
                    >
                        {twoFactorOptions.map((option) => {
                            const radio = getRadioProps({ value: option.type })                                    
                            return (
                                <RadioCard 
                                    type='badge'
                                    key={option.type} 
                                    radioProps={radio}
                                    boxProps={{ height: '90px', justifyContent: 'start', borderWidth: '0', padding: '30px 10px', px: 2, marginBottom: '8px' }}
                                >
                                    <Icon 
                                        as={option.icon} 
                                        size='sm' 
                                        marginRight={'1.5'} 
                                        color='gray.400'
                                    />
                                    <Box ml='2'>
                                        <Text fontSize='sm' color='gray.700' fontWeight={'semibold'}>{option.label}
                                            {radio.isChecked && twoFactorValue === radio.value && (
                                                <Badge variant={'outline'} size='sm' ml='2' colorScheme='green' borderRadius={'md'}>
                                                    Activated
                                                </Badge>
                                            )}
                                        </Text>
                                        <Text fontSize={'sm'} color='gray.500'>{option.description}</Text>
                                    </Box>
                                </RadioCard>
                            )
                        })}
                    </VStack>
                </Box>
            </Box>
        </Box>  
    )
}